'use client';

// Piezas de UI reutilizables para los ESQUEMAS de las decisiones iniciales
// (Indagación previa / Investigación), traídos de la maqueta CRISTIAN "modulo
// disciplinario 2" y adaptados a Tailwind + shadcn. Por ahora son presentacionales:
// el cálculo real de términos usa el MotorDeTerminos (días hábiles + festivos) y se
// cablea con la lógica más adelante.

import { AlarmClock } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/** Interruptor Sí/No con contenido condicional debajo (cuando el valor es `true`). */
export function SiNo({
  label, value, onChange, children,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      {/* Pregunta arriba y los botones Sí/No justo debajo (más cerca y legible). */}
      <span className="block text-sm">{label}</span>
      <div className="flex gap-2">
        <Button
          type="button" size="sm"
          variant={value === true ? 'default' : 'outline'}
          onClick={() => onChange(true)}
        >Sí</Button>
        <Button
          type="button" size="sm"
          variant={value === false ? 'default' : 'outline'}
          onClick={() => onChange(false)}
        >No</Button>
      </div>
      {value === true && children}
    </div>
  );
}

type Tono = 'ambar' | 'rojo' | 'azul' | 'verde';
const TONOS: Record<Tono, string> = {
  ambar: 'border-amber-500/40 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  rojo: 'border-red-500/40 bg-red-50 text-red-900 dark:bg-red-950/40 dark:text-red-200',
  azul: 'border-blue-500/40 bg-blue-50 text-blue-900 dark:bg-blue-950/40 dark:text-blue-200',
  verde: 'border-primary/40 bg-primary/5 text-primary',
};

/** Caja de "alerta de término" (semáforo de días/meses). Presentacional. */
export function AlertaTermino({ tono = 'ambar', children }: { tono?: Tono; children: React.ReactNode }) {
  return (
    <div className={cn('flex items-start gap-2 rounded-md border p-3 text-sm', TONOS[tono])}>
      <AlarmClock className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </div>
  );
}

/** Campo etiqueta + control (para reutilizar el patrón de la maqueta). */
export function Campo({
  label, requerido, children,
}: { label: string; requerido?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}{requerido && <span className="ml-0.5 text-destructive">*</span>}</Label>
      {children}
    </div>
  );
}

/** Bloque agrupador (equivalente al "subform" de la maqueta). */
export function SubForm({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
      <h4 className="text-sm font-semibold">{titulo}</h4>
      {children}
    </div>
  );
}
